import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, downloadHostAgent, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Server, Plug, Check, X, Copy, MonitorSmartphone, Loader2, Circle, Sparkles, Terminal,
  Github, Lock, GitBranch, RefreshCw, ExternalLink, Download, Rocket, Tag, Trash2,
} from "lucide-react";

const PROVIDER_LOGO = {
  anthropic: { label: "Anthropic", color: "#D97757" },
  openai: { label: "OpenAI", color: "#10A37F" },
  gemini: { label: "Google Gemini", color: "#4285F4" },
};

export default function Integrations() {
  const [hosts, setHosts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pairCode, setPairCode] = useState(null);
  const [removeHost, setRemoveHost] = useState(null); // host pending remove confirm

  async function load() {
    const [h, p] = await Promise.all([api.get("/hosts"), api.get("/providers")]);
    setHosts(h.data.hosts); setProviders(p.data.providers); setLoading(false);
  }
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gh = params.get("github");
    if (gh === "connected") toast.success("GitHub account connected");
    else if (gh === "error") toast.error("GitHub connection failed. Please try again.");
    if (gh) window.history.replaceState({}, "", "/integrations");
  }, []);

  async function genCode() {
    const { data } = await api.post("/hosts/pair-code");
    setPairCode(data.pairing_code);
  }
  async function toggleProvider(prov, connected) {
    try {
      if (connected) await api.delete(`/providers/${prov}`, { skipErrorToast: true });
      else await api.post("/providers/connect", { provider: prov }, { skipErrorToast: true });
      load();
    } catch (e) {
      // Connect now verifies the host CLI is present — surface the refusal instead
      // of optimistically flipping to "connected".
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  async function confirmRemoveHost() {
    const h = removeHost;
    setRemoveHost(null);
    if (!h) return;
    try {
      await api.delete(`/hosts/${h.host_id}`, { skipErrorToast: true });
      toast.success(`Removed ${h.name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const onlineHost = hosts.find((h) => h.online);

  if (loading) return <div className="flex-1 grid place-items-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="flex-1 overflow-y-auto cadence-scroll">
      <div className="max-w-4xl mx-auto px-8 py-10">
        <p className="label-caps mb-2">Connect</p>
        <h1 className="font-heading font-800 text-3xl tracking-tight text-gray-900">Integrations</h1>
        <p className="text-gray-500 mt-2 text-[15px]">Pair a local host and connect your model providers.</p>

        {/* Hosts */}
        <section className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-700 text-lg flex items-center gap-2"><Server className="w-4.5 h-4.5" /> Hosts</h2>
            <button data-testid="connect-host-button" onClick={genCode}
              className="bg-black text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-gray-900 inline-flex items-center gap-2">
              <Plug className="w-4 h-4" /> Connect a host
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Your local machine runs the Cadence host agent. Your files and model auth stay local — the web app reaches them over an encrypted relay.
          </p>
          <div className="space-y-2">
            {hosts.length === 0 && <div className="text-sm text-gray-400 border border-dashed rounded-xl p-6 text-center">No hosts paired yet.</div>}
            {hosts.map((h) => (
              <div key={h.id} data-testid="host-row" className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <MonitorSmartphone className="w-5 h-5 text-gray-500" />
                  <div>
                    <div className="font-600 text-sm text-gray-900">{h.name}</div>
                    <div className="text-xs text-gray-400 font-mono">{h.root_path || h.pairing_code}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-600 px-2.5 py-1 rounded-md ${h.online ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                    <Circle className={`w-2 h-2 ${h.online ? "fill-emerald-500 text-emerald-500 pulse-dot" : "fill-gray-400 text-gray-400"}`} />
                    {h.online ? "Online" : (h.status === "pending" ? "Awaiting pairing" : "Offline")}
                  </span>
                  {h.host_id && (
                    <button data-testid="host-remove" title="Remove host" onClick={() => setRemoveHost(h)}
                      className="text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-md p-1.5 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* GitHub */}
        <GitHubSection onlineHost={onlineHost} />

        {/* Providers */}
        <section className="mt-12">
          <h2 className="font-heading font-700 text-lg flex items-center gap-2 mb-3"><Sparkles className="w-4.5 h-4.5" /> Model providers</h2>
          <p className="text-sm text-gray-500 mb-4">
            Authentication happens on your host via each provider's own CLI (no API keys stored in the cloud).
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {providers.map((p) => {
              const meta = PROVIDER_LOGO[p.provider] || {};
              return (
                <div key={p.provider} data-testid="provider-card" className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col">
                  <div className="flex items-center justify-between">
                    <span className="w-8 h-8 rounded-lg grid place-items-center font-700 text-sm" style={{ background: `${meta.color}1a`, color: meta.color }}>
                      {meta.label?.slice(0, 1)}
                    </span>
                    {p.connected
                      ? <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-flex items-center gap-1"><Check className="w-3 h-3" /> Connected</span>
                      : <span className="text-xs text-gray-400">Not connected</span>}
                  </div>
                  <div className="font-600 text-sm mt-3 text-gray-900">{p.name}</div>
                  <div className="text-xs text-gray-400 mt-1 font-mono flex items-center gap-1"><Terminal className="w-3 h-3" /> {p.auth_hint}</div>
                  <div className="text-[11px] text-gray-400 mt-2">{p.models.length} models</div>
                  <button data-testid={`provider-toggle-${p.provider}`} onClick={() => toggleProvider(p.provider, p.connected)}
                    className={`mt-3 text-sm rounded-lg px-3 py-1.5 font-medium transition-colors ${p.connected ? "border border-gray-200 text-gray-700 hover:bg-gray-50" : "bg-black text-white hover:bg-gray-900"}`}>
                    {p.connected ? "Disconnect" : "Connect"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Infrastructure & domains (referral-aware) */}
      <InfraSection />

      {pairCode && <PairModal code={pairCode} onClose={() => setPairCode(null)} />}

      <AlertDialog open={!!removeHost} onOpenChange={(open) => { if (!open) setRemoveHost(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeHost?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This unpairs the host from your account. You can pair it again anytime with a new code.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="host-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="host-remove-confirm" onClick={confirmRemoveHost}
              className="bg-red-600 text-white hover:bg-red-700">
              Remove host
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfraSection() {
  const [items, setItems] = useState(null);
  const [disclosure, setDisclosure] = useState("");

  useEffect(() => {
    api.get("/affiliates")
      .then(({ data }) => { setItems(data.affiliates || []); setDisclosure(data.disclosure || ""); })
      .catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <div className="max-w-4xl mx-auto px-8 pb-16" data-testid="infra-section">
      <section className="mt-2">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-700 text-lg flex items-center gap-2">
            <Rocket className="w-4.5 h-4.5" /> Infrastructure & domains
          </h2>
          <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">
            <Tag className="w-3 h-3" /> Referral
          </span>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          When your build is ready to ship, deploy it and give it a custom domain. These partners run the
          hosting, databases, and domains Cadence apps need.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((it) => (
            <a
              key={it.key}
              data-testid={`affiliate-${it.key}`}
              href={it.url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="group bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-3 hover:border-gray-300 hover:shadow-sm transition-all"
            >
              <span className="w-9 h-9 rounded-lg grid place-items-center font-700 text-sm shrink-0"
                style={{ background: `${it.color}1a`, color: it.color }}>
                {it.name.slice(0, 1)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-600 text-sm text-gray-900">{it.name}</span>
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">{it.category}</span>
                  {it.sponsored && (
                    <span className="text-[9px] uppercase tracking-wide font-700 px-1.5 py-0.5 rounded"
                      style={{ background: `${it.color}1a`, color: it.color }}>Partner</span>
                  )}
                  <ExternalLink className="w-3 h-3 text-gray-300 ml-auto group-hover:text-gray-500 transition-colors" />
                </div>
                <div className="text-xs text-gray-500 mt-1">{it.blurb}</div>
                <div className="text-[11px] font-600 mt-2 inline-flex items-center gap-1"
                  style={{ color: it.color }}>
                  <Sparkles className="w-3 h-3" /> {it.perk}
                </div>
              </div>
            </a>
          ))}
        </div>
        {disclosure && (
          <p className="text-[11px] text-gray-400 mt-4" data-testid="affiliate-disclosure">{disclosure}</p>
        )}
      </section>
    </div>
  );
}

function GitHubSection({ onlineHost }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [repos, setRepos] = useState(null);
  const [reposLoading, setReposLoading] = useState(false);
  const [importing, setImporting] = useState(null);

  async function loadStatus() {
    try {
      const { data } = await api.get("/github/status");
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadStatus(); }, []);

  async function connect() {
    setBusy(true);
    // Open the tab synchronously inside the click gesture so pop-up blockers don't kill it.
    const popup = window.open("", "_blank");
    if (!popup) {
      toast.error("Allow pop-ups for this site, then click Connect GitHub again.");
      setBusy(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/github/start");
      popup.location = data.authorize_url;
      toast.message("Authorize Cadence in the new tab", { description: "This page updates automatically once you approve." });
      // GitHub can't render inside the preview iframe, so OAuth happens in a new tab.
      // Poll our backend for the linked status and update here when it lands.
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        try {
          const { data: st } = await api.get("/github/status");
          if (st.connected) {
            clearInterval(iv);
            setStatus(st);
            setBusy(false);
            toast.success("GitHub account connected");
            try { popup.close(); } catch (e) {}
          }
        } catch (e) { /* keep polling */ }
        if (tries > 90) { clearInterval(iv); setBusy(false); }
      }, 2000);
    } catch (e) {
      try { popup.close(); } catch (_) {}
      toast.error("Could not start GitHub authorization");
      setBusy(false);
    }
  }
  async function disconnect() {
    setBusy(true);
    try {
      await api.post("/github/disconnect");
      setRepos(null);
      await loadStatus();
      toast.success("GitHub disconnected");
    } finally {
      setBusy(false);
    }
  }
  async function loadRepos() {
    setReposLoading(true);
    try {
      const { data } = await api.get("/github/repos");
      setRepos(data.repositories || []);
    } catch (e) {
      toast.error("Failed to load repositories");
    } finally {
      setReposLoading(false);
    }
  }
  async function importRepo(r) {
    if (!onlineHost) { toast.error("Pair and start a host first (Connect a host above)."); return; }
    setImporting(r.id);
    try {
      const { data } = await api.post("/github/import", { host_id: onlineHost.host_id, full_name: r.full_name });
      toast.success(`Imported ${r.full_name}`);
      navigate(`/workspace/${data.project.id}?suggest=parallel`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImporting(null);
    }
  }

  const connected = status?.connected;

  return (
    <section className="mt-12" data-testid="github-section">
      <h2 className="font-heading font-700 text-lg flex items-center gap-2 mb-3"><Github className="w-4.5 h-4.5" /> GitHub</h2>
      <p className="text-sm text-gray-500 mb-4">
        Connect your GitHub account to import repositories and sync your worktree branches. Your access token is encrypted and never leaves the server.
      </p>

      <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="github-card">
        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Checking connection…</div>
        ) : connected ? (
          <div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {status.avatar_url
                  ? <img src={status.avatar_url} alt="" className="w-10 h-10 rounded-full" />
                  : <span className="w-10 h-10 rounded-full bg-gray-900 text-white grid place-items-center"><Github className="w-5 h-5" /></span>}
                <div>
                  <div className="font-600 text-sm text-gray-900" data-testid="github-login">
                    {status.github_name || status.github_login}
                    <span className="text-gray-400 font-normal ml-1.5">@{status.github_login}</span>
                  </div>
                  <div className="text-xs text-emerald-700 bg-emerald-50 inline-flex items-center gap-1 px-2 py-0.5 rounded-md mt-1">
                    <Check className="w-3 h-3" /> Connected
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="github-load-repos" onClick={loadRepos} disabled={reposLoading}
                  className="text-sm rounded-lg px-3 py-1.5 font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2">
                  {reposLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Load repos
                </button>
                <button data-testid="github-disconnect" onClick={disconnect} disabled={busy}
                  className="text-sm rounded-lg px-3 py-1.5 font-medium border border-gray-200 text-gray-700 hover:bg-gray-50">
                  Disconnect
                </button>
              </div>
            </div>

            {repos && (
              <div className="mt-5 border-t border-gray-100 pt-4">
                <div className="text-xs text-gray-400 mb-2">{repos.length} repositories</div>
                <div className="space-y-1.5 max-h-72 overflow-y-auto cadence-scroll" data-testid="github-repo-list">
                  {repos.map((r) => (
                    <div key={r.id} data-testid="github-repo-row" className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <GitBranch className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-800 font-mono truncate">{r.full_name}</span>
                        {r.private && <Lock className="w-3 h-3 text-gray-400 shrink-0" />}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button data-testid="github-import-repo" onClick={() => importRepo(r)} disabled={importing === r.id}
                          className="text-xs rounded-md px-2.5 py-1 font-medium bg-gray-900 text-white hover:bg-black disabled:opacity-50 inline-flex items-center gap-1">
                          {importing === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Import
                        </button>
                        <a href={r.html_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-900">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>
                  ))}
                  {repos.length === 0 && <div className="text-sm text-gray-400">No repositories found.</div>}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-gray-900 text-white grid place-items-center"><Github className="w-5 h-5" /></span>
              <div>
                <div className="font-600 text-sm text-gray-900">Not connected</div>
                <div className="text-xs text-gray-400">Authorize Cadence to access your repositories.</div>
              </div>
            </div>
            <button data-testid="github-connect" onClick={connect} disabled={busy}
              className="bg-black text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-gray-900 inline-flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Github className="w-4 h-4" />} Connect GitHub
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export function PairModal({ code, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const cmd = `pip install -r requirements.txt && python cadence_host.py --server wss://api.passo.link --code ${code} --root /path/to/project`;
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  async function download() {
    setDownloading(true);
    try {
      await downloadHostAgent();
      toast.success("Host agent downloaded");
    } catch (e) {
      toast.error("Could not download the host agent");
    } finally {
      setDownloading(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-700 text-lg">Pair a host</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Run the Cadence host agent on your machine with this one-time code.</p>
        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-3">
          <span className="font-mono text-2xl tracking-[0.3em] text-gray-900">{code}</span>
          <button onClick={() => { navigator.clipboard.writeText(code); toast.success("Code copied"); }} className="text-gray-500 hover:text-gray-900"><Copy className="w-4 h-4" /></button>
        </div>
        <button data-testid="download-host-agent" onClick={download} disabled={downloading}
          className="w-full mb-3 bg-black text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-gray-900 disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download host agent
        </button>
        <div className="bg-[#0A0A0A] text-gray-200 rounded-lg p-3 text-xs font-mono overflow-x-auto cadence-scroll">
          <div><span className="text-gray-500"># unzip cadence-host-agent.zip, then inside that folder:</span></div>
          <div className="whitespace-nowrap">{cmd}</div>
        </div>
        <p className="text-xs text-gray-400 mt-3">The agent connects outbound — no inbound ports, no firewall changes.</p>
      </div>
    </div>
  );
}
